import type {ISearchHit, ISearchResult, IFilterType} from "@features/Pages/SearchPage/Scripts/search.types";
import checkIcon from '@images/icons/icon-checkmark.svg';

import {
  searchInput,
  searchFiltersSection,
  searchResultSection,
  searchResultInfo,
  resultsToShow,
  resultsToAdd,
  currentLanguage,
  ancestors,
  hideFilters,
  textContent,
  isListingPage,
  initialSortOrderSelect,
  searchPageLoading,
  searchPageLoadingBottom
} from "@features/Pages/SearchPage/Scripts/search.setup";

import {
  getSearchResult,
  getPossibleFiltersForQuery,
  isSearchLoading
} from "@features/Pages/SearchPage/Scripts/search.api";
import type { ISearchRequestState } from "@features/Pages/SearchPage/Scripts/search.api";

let sortOrderSelect = initialSortOrderSelect;

/* Global variables */
export let query: string | null = null;
export let apiSkipAmount = 0 as number;
export let apiTakeAmount = parseInt(resultsToShow!, 10) as number;
export let selectedContentTypes: string[] = [];
export let selectedSubjects: string[] = [];
export let selectedEventStatus: string | null = null;
export let selectedSortOrder: "asc" | "desc" = "asc";
export let apiAncestors: string | null = ancestors || null;

const getSearchRequestState = (): ISearchRequestState => ({
  query,
  apiSkipAmount,
  apiTakeAmount,
  selectedContentTypes,
  selectedSubjects,
  selectedEventStatus,
  selectedSortOrder,
  apiAncestors
});

const allowedEventStatuses = new Set(["upcoming", "past"]);
const allowedSortOrders = new Set(["asc", "desc"]);

const normalizeEventStatus = (value: string | null): string | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  return allowedEventStatuses.has(normalized) ? normalized : null;
};

const normalizeSortOrder = (value: string | null): "asc" | "desc" | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!allowedSortOrders.has(normalized)) {
    return null;
  }

  return normalized as "asc" | "desc";
};

// Default event status for listing pages
if (isListingPage) {
  selectedEventStatus = "upcoming";
}

/* Functions – Scroll to results */
const scrollToSearchResults = (): void => {
  const target =
    (document.getElementById('search-result-list') as HTMLElement | null) ||
    (document.querySelector('.search-page__result') as HTMLElement | null);

  if (!target) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior: ScrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

  const header = document.querySelector('.header') as HTMLElement | null;
  const headerHeight = header?.offsetHeight ?? 0;
  const extraOffset = 16;

  requestAnimationFrame(() => {
    const top = window.scrollY + target.getBoundingClientRect().top - headerHeight - extraOffset;
    window.scrollTo({top: Math.max(0, top), behavior});
  });
};

/* Functions – Reset and clear DOM */
export const resetSearchResult = (): void => {
  searchInput.value = '';
  searchResultSection.classList.add('hidden');
  searchResultInfo.textContent = textContent.noKeywords;
};

const clearResultList = (): void => {
  const resultList: HTMLUListElement | null = document.querySelector('.search-page__result-list');
  const showMore: HTMLDivElement | null = document.querySelector('.search-page__show-more');
  document.querySelectorAll('.search-page__result-list').forEach(el => el.remove());

  if (resultList) {
    resultList.remove();
  }

  if (showMore) {
    showMore.remove();
  }
};

export const clearFilters = (): void => {
  searchFiltersSection.replaceChildren();
};

export const resetSelectedFilters = () => {
  selectedContentTypes = [];
  selectedSubjects = [];
};

export const setQuery = (value: string) => {
  query = value;
};

export const resetPagination = () => {
  apiSkipAmount = 0;
};

/* Functions – Render DOM */
const renderResultItems = (searchHits: ISearchHit[], resultList: HTMLUListElement): void => {
  searchHits.forEach(item => {
    const resultItem = document.createElement('li') as HTMLLIElement;
    resultItem.classList.add('search-page__result-item', 'site-page-alternative-partial', 'site-page-alternative-partial--search-hit');

    const contentWrapper = document.createElement('div') as HTMLDivElement;
    contentWrapper.classList.add('site-page-alternative-partial__content');

    const textContainer = document.createElement('div') as HTMLDivElement;
    textContainer.classList.add('site-page-alternative-partial__text-container');

    const textContentDiv = document.createElement('div') as HTMLDivElement;
    textContentDiv.classList.add('site-page-alternative-partial__text-content');

    const iconContainer = document.createElement('div') as HTMLDivElement;
    iconContainer.classList.add('site-page-alternative-partial__arrow-icon-container');

    const iconSpan = document.createElement('span') as HTMLSpanElement;
    iconSpan.classList.add('site-page-alternative-partial__arrow-icon', 'icon', 'icon--arrow-right');

    textContainer.appendChild(textContentDiv);
    iconContainer.appendChild(iconSpan);
    textContainer.appendChild(iconContainer);
    contentWrapper.appendChild(textContainer);

    /*** Date */
    const dateAndCategories = document.createElement('div') as HTMLDivElement;
    dateAndCategories.classList.add('site-page-alternative-partial__date-time-and-categories');

    const timeEl = document.createElement('time') as HTMLTimeElement;
    timeEl.classList.add('site-page-alternative-partial__date-time');
    const dateText = item.lastUpdatedDate.split(' ')[0];
    timeEl.setAttribute("data-date", item.lastUpdatedDateISO);
    timeEl.textContent = dateText;

    dateAndCategories.appendChild(timeEl);
    textContentDiv.appendChild(dateAndCategories);

    /*** Tags */
    if (item?.contentTypeName || item?.subjects.length) {
      const tagsList = document.createElement('ul') as HTMLUListElement;
      tagsList.classList.add('site-page-alternative-partial__category-list', 'tag-container');

      if (item?.contentTypeName) {
        const contentTypeTag = document.createElement('li') as HTMLLIElement;
        contentTypeTag.classList.add('site-page-alternative-partial__category-item', 'tag', 'tag--small', `tag-content-type-${item.contentTypeName}`);
        contentTypeTag.textContent = item.contentTypeDisplayName;
        tagsList.appendChild(contentTypeTag);
      }

      if (item?.subjects.length) {
        item.subjects.forEach(subject => {
          const subjectTag = document.createElement('li') as HTMLLIElement;
          subjectTag.classList.add('site-page-alternative-partial__category-item', 'tag', 'tag--small');
          subjectTag.textContent = subject;
          tagsList.appendChild(subjectTag);
        });
      }

      dateAndCategories.appendChild(tagsList);
    }

    /*** Header / Title */
    const heading = document.createElement('h3') as HTMLHeadingElement;
    heading.classList.add('site-page-alternative-partial__heading', 'heading');

    // Add icon if document
    if (item?.contentTypeName === 'document') {
      heading.classList.add('icon', 'icon--pdf');
    }

    /*** Link */
    const link = document.createElement('a') as HTMLAnchorElement;
    link.classList.add('site-page-alternative-partial__link');
    link.setAttribute('href', item.url);
    link.textContent = item.heading;
    heading.appendChild(link);
    textContentDiv.appendChild(heading);

    /*** Preamble */
    if (item.preamble) {
      const preamble = document.createElement('p') as HTMLParagraphElement;
      preamble.classList.add('site-page-alternative-partial__preamble');
      preamble.innerHTML = item.preamble;
      textContentDiv.appendChild(preamble);
    }

    resultItem.appendChild(contentWrapper);
    resultList.appendChild(resultItem);
  });
};

const renderContentTypeFilters = (filters: IFilterType[]): void => {
  // Container
  const filterContainer = document.createElement('div');
  filterContainer.classList.add('search-page__filters-container');

  // Label
  const filterTypeLabel = document.createElement('p');
  filterTypeLabel.id = 'content-type-filter-label';
  filterTypeLabel.classList.add('search-page__filter-type-label');

  const hiddenFilterLabel = document.createElement('span');
  hiddenFilterLabel.classList.add('sr-only');
  hiddenFilterLabel.textContent = "Filter: ";
  filterTypeLabel.appendChild(hiddenFilterLabel);

  const filterTypeLabelText = document.createElement('span');
  filterTypeLabelText.textContent = textContent.contentType;

  filterTypeLabel.appendChild(filterTypeLabelText);

  // Toggle button
  const toggleButton = document.createElement('button');
  toggleButton.classList.add('search-page__filter-toggle-button', 'search-page__filter-toggle-button--content-type');
  toggleButton.setAttribute('aria-expanded', 'false');

  // Spans in button
  const spanOpen = document.createElement('span');
  spanOpen.classList.add('search-page__filter-toggle-is-open');
  spanOpen.textContent = `${textContent.hide} ${textContent.contentTypeMultiple}`;

  const iconOpen = document.createElement('span');
  iconOpen.classList.add('icon', 'icon--minus');
  spanOpen.appendChild(iconOpen);

  const spanClosed = document.createElement('span');
  spanClosed.classList.add('search-page__filter-toggle-is-closed');
  spanClosed.textContent = `${textContent.show} ${textContent.contentTypeMultiple}`;

  const iconClosed = document.createElement('span');
  iconClosed.classList.add('icon', 'icon--plus');
  spanClosed.appendChild(iconClosed);

  toggleButton.appendChild(spanOpen);
  toggleButton.appendChild(spanClosed);

  // Button container
  const filterButtonContainer = document.createElement('div');
  filterButtonContainer.classList.add('search-page__filter-button-container', 'search-page__filter-button-container--hidden');
  filterButtonContainer.id = 'content-type-filter-container';

  toggleButton.addEventListener('click', () => {
    toggleButton.setAttribute('aria-expanded', toggleButton.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
    filterButtonContainer.classList.toggle('search-page__filter-button-container--hidden');
  });

  filterContainer.appendChild(filterTypeLabel);
  filterContainer.appendChild(toggleButton);
  filterContainer.appendChild(filterButtonContainer);
  searchFiltersSection.appendChild(filterContainer);

  // Create buttons
  filters.forEach((filter: IFilterType) => {
    const filterButton = document.createElement('button');
    filterButton.classList.add('search-page__filter-button', 'button', 'button--filter', `search-page__filter-button-${filter.term}`);
    filterButton.setAttribute('data-filter', filter.term);

    // No backend translation for content types
    filterButton.textContent = filter.displayName;
    filterButton.setAttribute('aria-pressed', 'false');
    filterButton.setAttribute('aria-controls', 'search-result-list');
    filterButton.setAttribute('aria-describedby', 'content-type-filter-label');

    // Check if filter is already selected
    if (selectedContentTypes.includes(filter.term)) {
      filterButton.classList.add('button--active');
      filterButton.setAttribute('aria-pressed', 'true');
    }

    filterButton.addEventListener('click', () => {
      // Filter was already selected
      if (filterButton.classList.contains('button--active')) {
        selectedContentTypes = selectedContentTypes?.filter(item => item !== filter.term);
        filterButton.classList.remove('button--active');
        filterButton.setAttribute('aria-pressed', 'false');
      } else {
        // Filter was not selected
        filterButton.classList.add('button--active');
        selectedContentTypes?.push(filter.term);
        filterButton.setAttribute('aria-pressed', 'true');
      }

      apiSkipAmount = 0;
      window.history.replaceState({}, '', buildQueryString());
      search();
    });

    filterButtonContainer.appendChild(filterButton);
  });
};

const renderSubjectFilters = (filters: IFilterType[]): void => {
  const filterContainer = document.createElement("div");
  filterContainer.classList.add("search-page__filters-container");
  if (isListingPage) {
    filterContainer.classList.add("listing-page__filters-container");
  } else {
    filterContainer.classList.add("search-page__filters-container--border-bottom");
  }

  // --- LABEL ---
  const filterTypeLabel = document.createElement("p");
  filterTypeLabel.id = "subjects-filter-label";
  filterTypeLabel.classList.add("search-page__filter-type-label");

  filterTypeLabel.textContent = textContent.subjects;

  // Listing page should show label on mobile
  if (isListingPage) {
    filterTypeLabel.classList.add("search-page__filter-type-label--always-visible");
  }

  filterContainer.appendChild(filterTypeLabel);

  // --- BUTTON CONTAINER ---
  const buttonContainer = document.createElement("div");
  buttonContainer.classList.add("search-page__filter-button-container");
  if (isListingPage) {
    buttonContainer.classList.add("listing-page__filter-button-container");
  }
  buttonContainer.id = "subject-filter-container";

  // On search page → start hidden + toggle
  if (!isListingPage) {
    buttonContainer.classList.add("search-page__filter-button-container--hidden");

    const toggleButton = document.createElement("button");
    toggleButton.classList.add("search-page__filter-toggle-button");
    toggleButton.setAttribute("aria-expanded", "false");

    // OPEN text
    const spanOpen = document.createElement("span");
    spanOpen.classList.add("search-page__filter-toggle-is-open");
    spanOpen.textContent = `${textContent.hide} ${textContent.subjectsMultiple}`;
    spanOpen.appendChild(Object.assign(document.createElement("span"), {className: "icon icon--minus"}));

    // CLOSED text
    const spanClosed = document.createElement("span");
    spanClosed.classList.add("search-page__filter-toggle-is-closed");
    spanClosed.textContent = `${textContent.show} ${textContent.subjectsMultiple}`;
    spanClosed.appendChild(Object.assign(document.createElement("span"), {className: "icon icon--plus"}));

    toggleButton.appendChild(spanOpen);
    toggleButton.appendChild(spanClosed);

    toggleButton.addEventListener("click", () => {
      const expanded = toggleButton.getAttribute("aria-expanded") === "true";
      toggleButton.setAttribute("aria-expanded", (!expanded).toString());
      buttonContainer.classList.toggle("search-page__filter-button-container--hidden");
    });

    filterContainer.appendChild(toggleButton);
  }

  // Add container now
  filterContainer.appendChild(buttonContainer);
  searchFiltersSection.appendChild(filterContainer);

  // --- ADD BUTTONS ---
  filters.forEach(filter => {
    const btn = document.createElement("button");
    btn.classList.add("search-page__filter-button", "button", "button--filter", "search-page__filter-button-default");
    btn.textContent = filter.displayName;
    btn.dataset.filter = filter.term;

    if (selectedSubjects.includes(filter.term)) {
      btn.classList.add("button--active");
    }

    if (isListingPage) {
      syncPrintCheckIcon(btn, btn.classList.contains("button--active"));
    }

    btn.addEventListener("click", () => {
      if (btn.classList.contains("button--active")) {
        selectedSubjects = selectedSubjects.filter(x => x !== filter.term);
        btn.classList.remove("button--active");
      } else {
        selectedSubjects.push(filter.term);
        btn.classList.add("button--active");
      }

      if (isListingPage) {
        syncPrintCheckIcon(btn, btn.classList.contains("button--active"));
      }

      apiSkipAmount = 0;
      window.history.replaceState({}, "", buildQueryString());
      search();
    });

    buttonContainer.appendChild(btn);
  });
};

const renderEventStatusFilters = () => {
  // Container
  const filterContainer = document.createElement("div");
  filterContainer.classList.add("search-page__filters-container", "listing-page__filters-container", "listing-page__filters-container--border-bottom", "search-page__filters-container--border-bottom");

  // Label
  const label = document.createElement("p");
  label.classList.add("search-page__filter-type-label");

  if (isListingPage) {
    label.classList.add("search-page__filter-type-label--always-visible");
  }

  label.textContent = textContent.filterTypeLabel || "Type";
  filterContainer.appendChild(label);

  // Button container
  const buttonContainer = document.createElement("div");
  buttonContainer.classList.add("search-page__filter-button-container");
  if (isListingPage) {
    buttonContainer.classList.add("listing-page__filter-button-container");
  }
  buttonContainer.id = 'event-status-filter-container';
  filterContainer.appendChild(buttonContainer);

  const statuses = [
    {term: "upcoming", label: textContent.eventUpcoming ?? "Kommande"},
    {term: "past", label: textContent.eventPast ?? "Passerat"}
  ];

  statuses.forEach(status => {
    const btn = document.createElement("button");
    btn.classList.add("search-page__filter-button", "button", "button--filter");
    btn.textContent = status.label;
    btn.dataset.filter = status.term;

    if (selectedEventStatus === status.term) {
      btn.classList.add("button--active");
    }
    syncPrintCheckIcon(btn, selectedEventStatus === status.term);

    btn.addEventListener("click", () => {
      // If user clicks the active button → do nothing
      if (selectedEventStatus === status.term) {
        return;
      }

      // Otherwise switch status
      selectedEventStatus = status.term;
      selectedSortOrder = status.term === "past" ? "desc" : "asc";
      syncSortOrderSelect();
      apiSkipAmount = 0;

      // Update button styles
      buttonContainer.querySelectorAll("button").forEach(b =>
        b.classList.remove("button--active")
      );
      btn.classList.add("button--active");

      buttonContainer.querySelectorAll("button").forEach(b => {
        syncPrintCheckIcon(b as HTMLButtonElement, b === btn);
      });

      window.history.replaceState({}, '', buildQueryString());
      search();
    });

    buttonContainer.appendChild(btn);
  });

  searchFiltersSection.appendChild(filterContainer);
};

const renderShowMoreButton = (searchHitsLeft: number): void => {
  const showMoreContainer = document.createElement('div');
  showMoreContainer.classList.add('search-page__show-more');

  const showMoreButton = document.createElement('button');
  showMoreButton.classList.add('search-page__show-more-button', 'button', 'button--primary');
  showMoreButton.setAttribute('type', 'button');

  // Update or remove the button
  updateShowMoreButton(showMoreButton, searchHitsLeft);

  showMoreButton.addEventListener('click', () => {
    addMoreResultItems(showMoreButton);
  });

  showMoreContainer.appendChild(showMoreButton);
  searchResultSection.appendChild(showMoreContainer);
};

const updateShowMoreButton = (showMoreButton: HTMLButtonElement, searchHitsLeft: number): void => {
  if (resultsToAdd) {
    if (searchHitsLeft <= 0) {
      showMoreButton.remove();
    } else {
      let showMoreNumber = apiTakeAmount;

      if (searchHitsLeft < showMoreNumber) {
        showMoreNumber = searchHitsLeft;
      }

      // Update button text
      showMoreButton.innerHTML = `${textContent.show} ${showMoreNumber} ${textContent.more}`;
      showMoreButton.appendChild(iconPlus());
    }
  } else {
    showMoreButton.textContent = textContent.showMore;
  }
};

const renderResult = (searchResult: ISearchResult): void => {
  clearResultList();

  if (isListingPage) {
    clearFilters();

    if (searchResult.subjects?.length) {
      renderSubjectFilters(searchResult.subjects);
    }

    renderEventStatusFilters();
  }

  const total = searchResult.totalHits ?? 0;
  updateSearchResultInfoText(isListingPage, searchResult.searchHits.length, total);

  if (searchResult.searchHtml && searchResult.searchHtml.trim().length > 0) {
    const resultList = document.createElement("div");
    resultList.classList.add("search-page__result-list");
    if (isListingPage) {
      resultList.classList.add("listing-page__result-list");
    }
    resultList.innerHTML = searchResult.searchHtml;
    searchResultSection.appendChild(resultList);

    // Listing-page-only result count logic
    if (isListingPage && searchResult.searchHits.length < searchResult.totalHits) {
      const searchHitsLeft = searchResult.totalHits - resultList.children.length;
      renderShowMoreButton(searchHitsLeft);
    }

    searchResultSection.classList.remove("hidden");
    return;
  }

  if (searchResult.totalHits > 0) {
    const resultList = document.createElement('ul') as HTMLUListElement;
    resultList.id = 'search-result-list';
    resultList.classList.add('search-page__result-list');
    if (isListingPage) {
      resultList.classList.add('listing-page__result-list');
    }
    searchResultSection.appendChild(resultList);

    renderResultItems(searchResult.searchHits, resultList);

    if (searchResult.searchHits.length < searchResult.totalHits) {
      const searchHitsLeft = searchResult.totalHits - resultList.children.length;
      renderShowMoreButton(searchHitsLeft);
    }
  } else {
    searchResultInfo.textContent = textContent.noResults;
  }

  searchResultSection.classList.remove('hidden');
  attachSortToggleHandler();
};

const updateSearchResultInfoText = (isListingPage: boolean, hits: number, total: number): void => {
  if (isListingPage) {
    searchResultInfo.innerHTML = `${textContent.showing} ${hits} ${textContent.event} ${textContent.ofTotally} ${total}`;
  } else if (total === 1) {
    searchResultInfo.innerHTML = `${total} ${textContent.resultFor} <strong>${query}</strong>.`;
  } else {
    searchResultInfo.innerHTML = `${textContent.showing} 1-${hits} ${textContent.ofTotally} ${total} <span class="sr-only">${textContent.resultsFor}: ${query}</span>`;
  }
};

export const renderWithoutResult = (): void => {
  clearResultList();
  clearFilters();

  searchInput.value = '';
  searchResultInfo.textContent = textContent.noKeywords;
  searchResultSection.classList.remove('hidden');
};

/* Functions - Search */
export const search = async (shouldScroll = true): Promise<void> => {
  const searchResult = await getSearchResult(getSearchRequestState(), toggleLoadingSpinner);
  if (searchResult) {
    renderResult(searchResult);
    if (shouldScroll) {
      scrollToSearchResults();
    }
  }
};

export const searchFromQuery = async (): Promise<void> => {
  const searchResult = await getSearchResult(getSearchRequestState(), toggleLoadingSpinner, true);

  if (searchResult) {
    renderResult(searchResult);
  }
};

export const searchFilters = async (): Promise<void> => {
  const filters = await getPossibleFiltersForQuery(query);

  clearFilters();

  if (isListingPage) {
    if (filters?.subjects.length) {
      renderSubjectFilters(filters.subjects);
    }
    renderEventStatusFilters();
    return;
  }

  if (filters?.contentTypes.length) {
    renderContentTypeFilters(filters.contentTypes);
  }
  if (filters?.subjects.length) {
    renderSubjectFilters(filters.subjects);
  }
};

export const searchFromQueryString = async (shouldScroll = false): Promise<void> => {
  const urlParams = new URLSearchParams(window.location.search) as URLSearchParams;
  const queryFromParam: string | null = urlParams.get('query');
  const takeFromParam: string | null = urlParams.get('take');
  const skipFromParam: string | null = urlParams.get('skip');
  const contentTypeFromParam = isListingPage ? null : urlParams.get("contentTypesOr");
  const subjectFromParam = isListingPage ? urlParams.get("categories") : urlParams.get("categoriesOr");
  selectedEventStatus = normalizeEventStatus(urlParams.get("eventStatus"));
  const sortFromParam = normalizeSortOrder(urlParams.get("sortOrder"));

  if (isListingPage && !selectedEventStatus) {
    selectedEventStatus = "upcoming";
  }

  selectedSortOrder = isListingPage
    ? sortFromParam ?? (selectedEventStatus === "past" ? "desc" : "asc")
    : sortFromParam ?? "asc";

  syncSortOrderSelect();

  const ancestorsParam: string | null = urlParams.get('ancestors');

  if (contentTypeFromParam) {
    selectedContentTypes = contentTypeFromParam.split(',');
  } else {
    selectedContentTypes = [];
  }

  if (subjectFromParam) {
    selectedSubjects = subjectFromParam.split(',');
  } else {
    selectedSubjects = [];
  }

  query = queryFromParam;

  if (query) {
    query = query.trim();
    searchInput.value = query;
  } else {
    query = '';
  }

  if (takeFromParam && !isNaN(parseInt(takeFromParam, 10))) {
    apiTakeAmount = parseInt(takeFromParam, 10);
  }

  if (skipFromParam && !isNaN(parseInt(skipFromParam, 10))) {
    apiSkipAmount = parseInt(skipFromParam, 10);
  }

  if (ancestorsParam) {
    apiAncestors = ancestorsParam;
  }

  if (isListingPage) {
    await search(shouldScroll);
    return;
  }

  // Default logic for normal search pages
  if (query.length > 0) {
    await searchFromQuery();
    if (!hideFilters) {
      await searchFilters();
    }
    if (shouldScroll) {
      scrollToSearchResults();
    }
  } else {
    renderWithoutResult();
  }
};

const buildQueryString = () => {
  const qs = new URLSearchParams();

  qs.set("query", query ?? "");
  qs.set("language", currentLanguage!);

  if (isListingPage) {
    qs.set("eventStatus", selectedEventStatus ?? "upcoming");
    qs.set("sortOrder", selectedSortOrder);

    if (selectedSubjects.length > 0) {
      qs.set("categories", selectedSubjects.join(","));
    }
  } else {
    qs.set("take", apiTakeAmount.toString());
    qs.set("skip", apiSkipAmount.toString());

    if (selectedContentTypes.length > 0) {
      qs.set("contentTypesOr", selectedContentTypes.join(","));
    }
    if (selectedSubjects.length > 0) {
      qs.set("categoriesOr", selectedSubjects.join(","));
    }
  }

  return `?${qs.toString()}`;
};

const addMoreResultItems = async (showMoreButton: HTMLButtonElement): Promise<void> => {
  apiSkipAmount += apiTakeAmount;

  const searchResult = await getSearchResult(getSearchRequestState(), toggleLoadingSpinner, false, true);

  if (!searchResult) {
    return;
  }

  const resultList = document.querySelector('.search-page__result-list') as HTMLUListElement;
  const total = searchResult.totalHits;

  //-------------------------------------------------------
  // LISTING PAGE PAGINATION (articles inside HTML wrapper)
  //-------------------------------------------------------
  if (isListingPage) {
    // Append backend HTML directly
    resultList.insertAdjacentHTML("beforeend", searchResult.searchHtml);
  }
    //---------------------------------------------
    // SITE SEARCH PAGINATION (original logic, UL)
  //---------------------------------------------
  else {
    renderResultItems(searchResult.searchHits, resultList);
  }

  const previousCount = resultList.children.length;

  // Update result info text
  updateSearchResultInfoText(isListingPage, previousCount, total);

  // Update or remove the button
  updateShowMoreButton(showMoreButton, total - previousCount);

  const firstNew = resultList.children[previousCount] as HTMLElement;
  firstNew?.querySelector('a')?.focus();

  // Update URL
  window.history.replaceState({}, "", buildQueryString());
};

export const toggleLoadingSpinner = (isLoadMore: boolean): void => {
  const loading = isSearchLoading();
  if (isLoadMore) {
    if (loading) {
      searchPageLoadingBottom.classList.remove('hidden');
    } else {
      searchPageLoadingBottom.classList.add('hidden');
    }
  } else {
    if (loading) {
      searchPageLoading.classList.remove('hidden');
      searchResultSection.classList.add('hidden');
    } else {
      searchPageLoading.classList.add('hidden');
      searchResultSection.classList.remove('hidden');
    }
  }
};

const syncSortOrderSelect = () => {
  if (sortOrderSelect) {
    sortOrderSelect.value = selectedSortOrder;
  }
};

export const attachSortToggleHandler = () => {
  const select = document.getElementById("sortOrder") as HTMLSelectElement | null;
  sortOrderSelect = select;

  if (!select) return;

  syncSortOrderSelect();

  select.onchange = () => {
    selectedSortOrder = normalizeSortOrder(select.value) ?? "asc";
    syncSortOrderSelect();

    apiSkipAmount = 0;

    const params = new URLSearchParams(window.location.search);
    params.set("sortOrder", selectedSortOrder);
    params.set("skip", "0");
    window.history.replaceState({}, "", "?" + params.toString());

    void search();
  };
};

// helper for adding icon again
const iconPlus = () => {
  const icon = document.createElement("span");
  icon.classList.add("search-page__show-more-button-icon", "icon", "icon--icon-plus");
  return icon;
};

// Add print icon (only shown in print + active)
const createPrintCheckIcon = (): HTMLImageElement => {
  const img = document.createElement("img");
  img.src = checkIcon;
  img.classList.add("print-icon", "print-icon--checkmark");
  img.alt = "";
  img.setAttribute("aria-hidden", "true");
  return img;
};

const syncPrintCheckIcon = (btn: HTMLButtonElement, isActive: boolean): void => {
  const existing = btn.querySelector("img.print-icon--checkmark");
  if (isActive) {
    if (!existing) {
      btn.appendChild(createPrintCheckIcon());
    }
  } else {
    existing?.remove();
  }
};
